import React, { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import {
  isDeliveryInFlight,
  SandboxWebhookDelivery,
} from '@/services/api/sandbox';
import {
  DeliveryStatusPill,
  formatTime,
  JsonBlock,
  RESTING_BUTTON,
  statusLabel,
} from './sandboxUi';

type Delivery = SandboxWebhookDelivery['delivery'];

const RETRYABLE: Delivery['status'][] = ['FAILED', 'DEAD', 'SKIPPED'];

/**
 * What the API's retry (`POST …/webhook/retry`) would do right now, from the
 * rows on screen — the same rules, so the button is only offered where the
 * call is accepted. Only the latest event (highest sequence) is retried; it is
 * refused once delivered, while queued, and when the shipment moved on after
 * it (no endpoint was active then, so its status changes wrote no webhook).
 */
type RetryTarget =
  | { kind: 'retryable'; row: SandboxWebhookDelivery }
  | { kind: 'outdated'; row: SandboxWebhookDelivery }
  | null;

const retryTargetOf = (
  deliveries: SandboxWebhookDelivery[],
  shipmentStatus: string | undefined
): RetryTarget => {
  if (deliveries.length === 0) return null;
  const seq = Math.max(...deliveries.map((d) => d.sequence));
  const latest = deliveries.filter((d) => d.sequence === seq);
  if (latest.some((d) => d.delivery.status === 'DONE')) return null;
  if (latest.some(isDeliveryInFlight)) return null;
  // The copy on the endpoint still active, else the one the API copies over.
  const row = latest.find((d) => !d.endpoint?.disabled) ?? latest[0];
  if (!RETRYABLE.includes(row.delivery.status)) return null;
  if (shipmentStatus && row.eventStatus && row.eventStatus !== shipmentStatus) {
    return { kind: 'outdated', row };
  }
  return { kind: 'retryable', row };
};

/** What the last manual retry got — kept on its row until the next one. */
export interface WebhookRetryResult {
  /** The row it belongs to (`eventId:endpointId`). */
  rowKey: string;
  /** When the retry was sent (ISO). */
  at: string;
  outcome: 'delivered' | 'failed' | 'refused' | 'error';
  title: string;
  /** The endpoint's answer (`HTTP 404: …` / network error) or the API's
   *  reason for refusing. */
  detail: string | null;
}

export interface WebhookRetryControl {
  /** Sends the latest webhook again; `rowKey` is the row it was asked from. */
  onRetry: (rowKey: string) => void;
  /** The retry call is out. */
  busy: boolean;
  /** Seconds until the next retry is allowed (0 = now). */
  waitSeconds: number;
  /** No active Sandbox endpoint (the setup check): the API would refuse the
   *  retry (400), so the callout points to adding one instead. */
  noEndpoint?: boolean;
  onAddEndpoint?: () => void;
  /** The last retry's outcome, shown inside its row. */
  result?: WebhookRetryResult | null;
}

/** Retry answers that are a notice, not an error. */
export const SOFT_REFUSAL: Record<number, string> = {
  409: 'Nothing to retry',
  429: 'Retried a moment ago',
};

/**
 * What a `POST …/webhook/retry` answer means for its row — a refusal (with
 * the API's reason), or the delivery it made (delivered / failed again).
 * `rowKey` is the row it was asked from; a delivery names its own row.
 */
export const retryResultOf = (
  answer: { status: number; response: unknown },
  rowKey: string,
  at: string
): WebhookRetryResult => {
  if (answer.status >= 400) {
    const body = answer.response as { error?: string } | undefined;
    const notice = SOFT_REFUSAL[answer.status];
    return {
      rowKey,
      at,
      outcome: notice ? 'refused' : 'error',
      title: notice ?? 'Retry refused',
      detail: body?.error ?? `HTTP ${answer.status}`,
    };
  }
  const row = (answer.response as { data?: SandboxWebhookDelivery })?.data;
  const delivered = row?.delivery.status === 'DONE';
  return {
    rowKey: row ? deliveryRowKey(row) : rowKey,
    at,
    outcome: delivered ? 'delivered' : 'failed',
    title: delivered
      ? 'Delivered: your endpoint accepted it'
      : 'Your endpoint failed again',
    detail: delivered ? null : (row?.delivery.lastError ?? null),
  };
};

/** The one-word cause of a delivery that did not arrive (`HTTP 404`,
 *  `Network error`), or null. */
export const deliveryCause = (d: SandboxWebhookDelivery['delivery']) =>
  d.status === 'DONE' ? null : causeChip(lastAnswerOf(d.lastError));

/** The key a row and a retry result share. */
export const deliveryRowKey = (d: SandboxWebhookDelivery) =>
  `${d.eventId}:${d.endpoint?.id ?? 'none'}`;

/** Newest event first; within one event, the newest copy first — a retry to
 *  the active endpoint is newer than the copy to the endpoint it replaced. */
const newestFirst = (deliveries: SandboxWebhookDelivery[]) =>
  [...deliveries].sort(
    (a, b) =>
      b.sequence - a.sequence ||
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

/** Above the list: the latest webhook did not arrive, and what can be done. */
const RetryCallout: React.FC<{
  target: NonNullable<RetryTarget>;
  retry?: WebhookRetryControl;
}> = ({ target, retry }) => {
  const { row } = target;
  const event = `#${row.sequence} ${
    row.eventStatus ? statusLabel(row.eventStatus) : row.eventType
  }`;
  if (target.kind === 'outdated') {
    return (
      <div className="mb-3 flex gap-2.5 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3 text-text-sub-600">
        <i
          className="ri-information-line text-base leading-5"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text-strong-950">
            Latest webhook is out of date
          </p>
          <p className="mt-0.5 text-xs">
            {event}. The shipment changed after it while no endpoint was active,
            so retrying would send an old state. GET shipment detail shows where
            it stands now.
          </p>
        </div>
      </div>
    );
  }
  const cause = causeChip(lastAnswerOf(row.delivery.lastError));
  return (
    // The text keeps 16rem: on a phone the button drops under it instead of
    // squeezing it into a narrow column.
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-warning-base/30 bg-warning-lighter px-4 py-3">
      <div className="flex min-w-[16rem] flex-1 gap-2.5 text-warning-dark">
        <i
          className="ri-error-warning-line text-base leading-5"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {row.delivery.status === 'SKIPPED'
              ? 'Latest webhook not sent'
              : 'Latest webhook not delivered'}
            {cause ? ` — ${cause}` : ''}
          </p>
          <p className="mt-0.5 text-xs">
            {event}. Retry sends this event again now, with the same id, so your
            system can dedupe it.
            {retry && !retry.noEndpoint && ' Retry it from its row below.'}
          </p>
        </div>
      </div>
      {retry?.noEndpoint &&
        (retry.onAddEndpoint ? (
          <Button variant="white" size="small" onClick={retry.onAddEndpoint}>
            <i className="ri-add-line mr-1.5" />
            Add a webhook endpoint
          </Button>
        ) : (
          <p className="text-xs font-medium text-warning-dark">
            Add a Sandbox webhook endpoint to retry.
          </p>
        ))}
    </div>
  );
};

/** The Retry button on the latest webhook's row. */
const RetryButton: React.FC<{
  retry: WebhookRetryControl;
  rowKey: string;
}> = ({ retry, rowKey }) => {
  const resting = !retry.busy && retry.waitSeconds > 0;
  return (
    <Button
      variant="white"
      size="small"
      disabled={retry.busy || retry.waitSeconds > 0}
      onClick={() => retry.onRetry(rowKey)}
      className={resting ? RESTING_BUTTON : ''}
    >
      <i className="ri-send-plane-line mr-1.5" />
      {retry.busy
        ? 'Retrying…'
        : retry.waitSeconds > 0
          ? `Retry in ${retry.waitSeconds}s`
          : 'Retry now'}
    </Button>
  );
};

const RESULT_TONE: Record<WebhookRetryResult['outcome'], string> = {
  delivered: 'border-success-base/30 bg-success-lighter text-success-dark',
  failed: 'border-warning-base/30 bg-warning-lighter text-warning-dark',
  refused: 'border-warning-base/30 bg-warning-lighter text-warning-dark',
  error: 'border-error-base/30 bg-error-lighter text-error-dark',
};

/** Inside the row: what the last manual retry got back. */
const RetryResultBox: React.FC<{ result: WebhookRetryResult }> = ({
  result,
}) => {
  const answer =
    result.outcome === 'failed' ? lastAnswerOf(result.detail) : null;
  return (
    <div
      className={`rounded-lg border px-4 py-3 text-xs ${RESULT_TONE[result.outcome]}`}
      role="status"
    >
      <p className="font-semibold">
        Manual retry {formatTime(result.at)} — {result.title}
      </p>
      {answer?.kind === 'http' ? (
        <div className="mt-2 space-y-2">
          <span className="inline-flex rounded-full bg-bg-white-0 px-2.5 py-0.5 font-mono font-semibold">
            HTTP {answer.status}
          </span>
          {answer.body !== '' && (
            <JsonBlock
              label="Response body (first 300 characters)"
              value={answer.body}
              maxHeight="max-h-48"
              wrap
            />
          )}
        </div>
      ) : (
        result.detail && (
          <p className="mt-1 break-words font-mono">{result.detail}</p>
        )
      )}
    </div>
  );
};

/**
 * What the last attempt got back, split out of `delivery.lastError`:
 * `HTTP 404: <the partner's answer, first 300 chars>`, a bare `HTTP 500`, or a
 * network failure (`fetch failed: getaddrinfo ENOTFOUND …`). A JSON answer is
 * parsed so it reads formatted; a cut-off one stays text.
 */
type LastAnswer =
  | { kind: 'http'; status: number; body: unknown }
  | { kind: 'network'; message: string };

const lastAnswerOf = (lastError: string | null): LastAnswer | null => {
  if (!lastError) return null;
  const http = /^HTTP (\d{3})(?::\s*([\s\S]*))?$/.exec(lastError);
  if (!http) return { kind: 'network', message: lastError };
  const raw = http[2] ?? '';
  let body: unknown = raw;
  try {
    body = raw ? JSON.parse(raw) : '';
  } catch {
    // Not JSON, or cut at 300 characters — shown as sent.
  }
  return { kind: 'http', status: Number(http[1]), body };
};

/** The one-word cause on the collapsed row. */
const causeChip = (answer: LastAnswer | null) =>
  answer === null
    ? null
    : answer.kind === 'http'
      ? `HTTP ${answer.status}`
      : 'Network error';

/** When the row's state happened or will happen next. */
const whenText = (d: Delivery, createdAt: string) => {
  switch (d.status) {
    case 'DONE':
      return `Delivered ${formatTime(d.deliveredAt)}`;
    case 'FAILED':
      // "Auto-": the automatic schedule, apart from any manual retry.
      return d.nextRetryAt
        ? `Auto-retry ${formatTime(d.nextRetryAt)}`
        : 'Auto-retrying';
    case 'DEAD':
      return 'No more auto-retries';
    default:
      return formatTime(createdAt);
  }
};

/** The expanded row's right side: what the endpoint answered, or why there is
 *  no answer. */
const EndpointAnswer: React.FC<{ d: Delivery }> = ({ d }) => {
  const answer = lastAnswerOf(d.lastError);
  if (d.status === 'DONE') {
    return (
      <div className="rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-4 py-3 text-xs text-text-sub-600">
        <span className="mr-2 inline-flex rounded-full bg-success-lighter px-2.5 py-0.5 font-mono font-semibold text-success-dark">
          2xx
        </span>
        Your endpoint accepted it {formatTime(d.deliveredAt)}.
      </div>
    );
  }
  if (!answer) {
    return (
      <div className="rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-4 py-3 text-xs text-text-sub-600">
        {d.status === 'SKIPPED'
          ? 'Not sent — the endpoint was disabled before it went out.'
          : 'Not sent yet — no answer to show.'}
      </div>
    );
  }
  if (answer.kind === 'network') {
    return (
      <div className="rounded-lg border border-error-base/30 bg-error-lighter px-4 py-3 text-xs text-error-dark">
        <p className="font-medium">Your endpoint was never reached</p>
        <p className="mt-1 break-words font-mono">{answer.message}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <span className="inline-flex rounded-full bg-error-lighter px-2.5 py-0.5 font-mono text-xs font-semibold text-error-dark">
        HTTP {answer.status}
      </span>
      {answer.body === '' ? (
        <p className="text-xs text-text-sub-600">Empty response body.</p>
      ) : (
        <JsonBlock
          label="Response body (first 300 characters)"
          value={answer.body}
          maxHeight="max-h-64"
          wrap
        />
      )}
    </div>
  );
};

/**
 * Every webhook this shipment produced, as sent to each endpoint — newest
 * event first. A row is a summary (status, cause, when); it expands to the
 * exact body that was POSTed beside what the endpoint answered.
 */
const WebhookDeliveriesPanel: React.FC<{
  deliveries: SandboxWebhookDelivery[] | null;
  loading: boolean;
  /** The shipment's status now — a latest webhook saying otherwise is stale. */
  shipmentStatus?: string;
  retry?: WebhookRetryControl;
}> = ({ deliveries, loading, shipmentStatus, retry }) => {
  const [open, setOpen] = useState<string | null>(null);
  // A fresh retry result opens its row, so the answer is in view.
  const resultKey = retry?.result
    ? `${retry.result.rowKey}@${retry.result.at}`
    : null;
  useEffect(() => {
    if (resultKey) setOpen(resultKey.split('@')[0]);
  }, [resultKey]);

  if (deliveries === null) {
    return (
      <p className="py-6 text-center text-sm text-text-sub-600">
        {loading ? 'Loading webhooks…' : 'Webhooks could not be loaded.'}
      </p>
    );
  }
  if (deliveries.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-sm font-medium text-text-strong-950">
          No webhooks sent for this shipment
        </p>
        <p className="mt-1 text-xs text-text-sub-600">
          Events are only sent to endpoints active at the time — an event from
          before your Sandbox endpoint existed is never sent.
        </p>
      </div>
    );
  }

  const queued = deliveries.filter(isDeliveryInFlight).length;
  const target = retryTargetOf(deliveries, shipmentStatus);
  const targetKey =
    target?.kind === 'retryable' ? deliveryRowKey(target.row) : null;
  const latestSeq = Math.max(...deliveries.map((d) => d.sequence));
  const rows = newestFirst(deliveries);

  return (
    <div>
      {queued > 0 && (
        <p className="mb-3 rounded-lg bg-bg-weak-50 px-3 py-2 text-xs text-text-sub-600">
          {queued === 1 ? '1 webhook is' : `${queued} webhooks are`} still
          queued — they usually leave within seconds. Use Refresh to update.
        </p>
      )}
      {target && <RetryCallout target={target} retry={retry} />}
      <p className="mb-3 text-xs text-text-sub-600">
        Signed with your endpoint secret in{' '}
        <code className="font-mono">x-dash-signature</code>. Dedupe on{' '}
        <code className="font-mono">data.event.id</code>, order by{' '}
        <code className="font-mono">data.event.sequence</code>.
      </p>
      <ul className="divide-y divide-stroke-soft-200 rounded-lg border border-stroke-soft-200">
        {rows.map((d) => {
          const key = deliveryRowKey(d);
          const expanded = open === key;
          const result = retry?.result?.rowKey === key ? retry.result : null;
          const offerRetry = key === targetKey && retry && !retry.noEndpoint;
          const cause =
            d.delivery.status === 'DONE'
              ? null
              : causeChip(lastAnswerOf(d.delivery.lastError));
          return (
            <li key={key}>
              <div className="flex flex-wrap items-center hover:bg-bg-weak-50">
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : key)}
                  aria-expanded={expanded}
                  className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left"
                >
                  <span className="w-8 font-mono text-xs tabular-nums text-text-soft-400">
                    #{d.sequence}
                  </span>
                  <span className="min-w-[110px] text-sm font-medium text-text-strong-950">
                    {d.eventStatus ? statusLabel(d.eventStatus) : d.eventType}
                  </span>
                  <DeliveryStatusPill status={d.delivery.status} />
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-text-sub-600">
                    {d.endpoint ? d.endpoint.url : 'endpoint removed'}
                    {d.endpoint?.disabled && ' (disabled)'}
                  </span>
                  {cause && (
                    <span className="rounded bg-error-lighter px-1.5 py-0.5 font-mono text-[11px] font-semibold text-error-dark">
                      {cause}
                    </span>
                  )}
                  <span className="text-xs tabular-nums text-text-sub-600">
                    {whenText(d.delivery, d.createdAt)}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-text-soft-400 transition-transform ${
                      expanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {offerRetry && (
                  <div className="px-4 pb-3 sm:pb-0 sm:pl-0">
                    <RetryButton retry={retry} rowKey={key} />
                  </div>
                )}
              </div>
              {expanded && (
                <div className="space-y-3 bg-bg-weak-50 px-4 pb-4 pt-2">
                  {result && <RetryResultBox result={result} />}
                  <p className="text-xs text-text-sub-600">
                    {d.delivery.attempts} attempt
                    {d.delivery.attempts === 1 ? '' : 's'}
                    {d.delivery.status === 'FAILED' && d.delivery.nextRetryAt
                      ? ` · next auto-retry ${formatTime(d.delivery.nextRetryAt)}`
                      : ''}
                    {d.delivery.status === 'DEAD'
                      ? ' · no next retry: it reached the automatic retry limit — only a manual retry can send it now'
                      : ''}
                  </p>
                  {d.sequence < latestSeq &&
                    RETRYABLE.includes(d.delivery.status) && (
                      <p className="text-xs text-text-sub-600">
                        Only the latest webhook can be retried — it carries the
                        shipment as it stands now, so this older event is
                        covered by it.
                      </p>
                    )}
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div className="min-w-0 space-y-2">
                      <p className="text-xs font-medium text-text-strong-950">
                        Sent to your endpoint
                      </p>
                      <JsonBlock label="Payload" value={d.payload} />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <p className="text-xs font-medium text-text-strong-950">
                        {d.delivery.status === 'DONE'
                          ? 'Your endpoint answered'
                          : 'Last answer from your endpoint'}
                      </p>
                      <EndpointAnswer d={d.delivery} />
                    </div>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WebhookDeliveriesPanel;
